import * as React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { BetupFeedChannelSubscriberDayDto } from '../../models/dtos/BetupFeedChannelDto';
import { formatCount } from './BetupFeedShared';

/** Categorical slots 1 and 2 of the dataviz reference palette (light): validated as an adjacent pair. */
export const SUBSCRIBED_COLOR = '#2a78d6';
export const UNSUBSCRIBED_COLOR = '#eb6834';
const TOTAL_COLOR = '#52514e';
const GRID_COLOR = '#e4e3df';
const TEXT_SECONDARY = '#52514e';
const SELECTED_FILL = 'rgba(9, 137, 227, 0.12)';
const HOVER_FILL = 'rgba(0, 0, 0, 0.05)';

const MARGIN = { left: 48, right: 12 };
const BARS_HEIGHT = 170;
const GAP = 28;
const TOTAL_HEIGHT = 100;
const AXIS_HEIGHT = 22;
const HEIGHT = BARS_HEIGHT + GAP + TOTAL_HEIGHT + AXIS_HEIGHT;

/** "Oct 2" for a yyyy-MM-dd day (the day itself, no time-zone shift). */
export function shortDay(day: string): string {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Fri, Oct 2, 2026". */
export function longDay(day: string): string {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
    const ref = React.useRef<T>(null);
    const [width, setWidth] = React.useState(0);
    React.useLayoutEffect(() => {
        const element = ref.current;
        if (element == null) return;
        setWidth(element.clientWidth);
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    return [ref, width];
}

/** A bar growing from the baseline: square at the baseline, its far end rounded (r ≤ 4). */
function barPath(x: number, width: number, baseline: number, end: number): string {
    const up = end < baseline;
    const height = Math.abs(baseline - end);
    const r = Math.min(4, width / 2, height);
    if (up) {
        return `M${x},${baseline} V${end + r} Q${x},${end} ${x + r},${end} H${x + width - r} ` +
            `Q${x + width},${end} ${x + width},${end + r} V${baseline} Z`;
    }
    return `M${x},${baseline} V${end - r} Q${x},${end} ${x + r},${end} H${x + width - r} ` +
        `Q${x + width},${end} ${x + width},${end - r} V${baseline} Z`;
}

/** Up to [count] evenly spread indexes of [length] items, first and last included. */
function tickIndexes(length: number, count: number): number[] {
    if (length <= count) return Array.from({ length }, (_, i) => i);
    const step = (length - 1) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(i * step));
}

function niceMax(value: number): number {
    if (value <= 0) return 1;
    const power = Math.pow(10, Math.floor(Math.log10(value)));
    const scaled = value / power;
    const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
    return nice * power;
}

/**
 * Subscribes (up) and unsubscribes (down) per day over the total subscribers at the end of each day: two plots on one
 * x axis, one hover column across both with a tooltip; a click picks the day ([onSelect], again = clear).
 */
export default function ChannelSubscriberChart({ days, selectedDay, onSelect }: {
    days: BetupFeedChannelSubscriberDayDto[];
    selectedDay: string | null;
    onSelect: (day: string | null) => void;
}) {
    const [ref, width] = useWidth<HTMLDivElement>();
    const [hover, setHover] = React.useState<number | null>(null);

    const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const band = days.length > 0 ? plotWidth / days.length : 0;
    const barGap = band > 6 ? 2 : 0;
    const barWidth = Math.max(1, band - barGap);
    const xOf = (i: number) => MARGIN.left + i * band;

    const maxUp = niceMax(Math.max(0, ...days.map(d => d.subscribed)));
    const maxDown = Math.max(...days.map(d => d.unsubscribed), 0) > 0 ? niceMax(Math.max(...days.map(d => d.unsubscribed))) : 0;
    const barsTop = 8;
    const barsScale = (BARS_HEIGHT - barsTop) / (maxUp + maxDown);
    const baseline = barsTop + maxUp * barsScale;
    const yBar = (value: number) => baseline - value * barsScale;

    const totals = days.map(d => d.total);
    const totalMin = totals.length > 0 ? Math.min(...totals) : 0;
    const totalMax = totals.length > 0 ? Math.max(...totals) : 0;
    const pad = Math.max(1, Math.round((totalMax - totalMin) * 0.1));
    const lo = Math.max(0, totalMin - pad);
    const hi = totalMax + pad;
    const totalTop = BARS_HEIGHT + GAP;
    const yTotal = (value: number) => totalTop + TOTAL_HEIGHT - ((value - lo) / Math.max(1, hi - lo)) * TOTAL_HEIGHT;
    const linePoints = days.map((d, i) => `${xOf(i) + band / 2},${yTotal(d.total)}`).join(' ');

    const hovered = hover != null ? days[hover] : null;
    const tooltipLeft = hover != null ? xOf(hover) + band / 2 : 0;

    return (
        <Box>
            <Stack direction="row" gap={2.5} flexWrap="wrap" sx={{ mb: 1 }}>
                <LegendSwatch color={SUBSCRIBED_COLOR} label="Subscribed" />
                <LegendSwatch color={UNSUBSCRIBED_COLOR} label="Unsubscribed" />
                <LegendSwatch color={TOTAL_COLOR} label="Total subscribers (end of day)" line />
            </Stack>
            <Box ref={ref} sx={{ position: 'relative', width: '100%', height: HEIGHT, userSelect: 'none' }}
                onMouseLeave={() => setHover(null)}>
                {width > 0 && days.length > 0 && (
                    <svg width={width} height={HEIGHT} role="img"
                        aria-label="Subscribes and unsubscribes per day, and the total subscribers">
                        {/* Recessive grid: the bars' zero line, their top / bottom values, the total's range. */}
                        <line x1={MARGIN.left} x2={width - MARGIN.right} y1={yBar(maxUp)} y2={yBar(maxUp)} stroke={GRID_COLOR} />
                        {maxDown > 0 && (
                            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={yBar(-maxDown)} y2={yBar(-maxDown)} stroke={GRID_COLOR} />
                        )}
                        <line x1={MARGIN.left} x2={width - MARGIN.right} y1={yTotal(hi)} y2={yTotal(hi)} stroke={GRID_COLOR} />
                        <line x1={MARGIN.left} x2={width - MARGIN.right} y1={yTotal(lo)} y2={yTotal(lo)} stroke={GRID_COLOR} />
                        <AxisLabel y={yBar(maxUp)} text={`+${formatCount(maxUp)}`} />
                        <AxisLabel y={baseline} text="0" />
                        {maxDown > 0 && <AxisLabel y={yBar(-maxDown)} text={`−${formatCount(maxDown)}`} />}
                        <AxisLabel y={yTotal(hi)} text={formatCount(hi)} />
                        <AxisLabel y={yTotal(lo)} text={formatCount(lo)} />

                        {days.map((d, i) => (
                            <React.Fragment key={d.date}>
                                {d.date === selectedDay && (
                                    <rect x={xOf(i)} y={0} width={band} height={totalTop + TOTAL_HEIGHT} fill={SELECTED_FILL} />
                                )}
                                {hover === i && d.date !== selectedDay && (
                                    <rect x={xOf(i)} y={0} width={band} height={totalTop + TOTAL_HEIGHT} fill={HOVER_FILL} />
                                )}
                                {d.subscribed > 0 && (
                                    <path d={barPath(xOf(i) + barGap / 2, barWidth, baseline, yBar(d.subscribed))} fill={SUBSCRIBED_COLOR} />
                                )}
                                {d.unsubscribed > 0 && (
                                    <path d={barPath(xOf(i) + barGap / 2, barWidth, baseline, yBar(-d.unsubscribed))} fill={UNSUBSCRIBED_COLOR} />
                                )}
                            </React.Fragment>
                        ))}
                        <line x1={MARGIN.left} x2={width - MARGIN.right} y1={baseline} y2={baseline} stroke={TEXT_SECONDARY} strokeWidth={1} />

                        <polyline points={linePoints} fill="none" stroke={TOTAL_COLOR} strokeWidth={2} strokeLinejoin="round" />
                        {hover != null && (
                            <circle cx={xOf(hover) + band / 2} cy={yTotal(days[hover].total)} r={4.5}
                                fill={TOTAL_COLOR} stroke="#fff" strokeWidth={2} />
                        )}

                        {tickIndexes(days.length, Math.max(2, Math.min(8, Math.floor(plotWidth / 80)))).map(i => (
                            <text key={i} x={xOf(i) + band / 2} y={HEIGHT - 6} fontSize={11} fill={TEXT_SECONDARY}
                                textAnchor={i === 0 ? 'start' : i === days.length - 1 ? 'end' : 'middle'}>
                                {shortDay(days[i].date)}
                            </text>
                        ))}

                        {/* Hit targets: the whole column of a day, both plots. */}
                        {days.map((d, i) => (
                            <rect key={d.date} x={xOf(i)} y={0} width={band} height={totalTop + TOTAL_HEIGHT} fill="transparent"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHover(i)}
                                onClick={() => onSelect(d.date === selectedDay ? null : d.date)} />
                        ))}
                    </svg>
                )}
                {hovered && (
                    <Paper elevation={3} sx={{
                        position: 'absolute', top: 4, pointerEvents: 'none', px: 1.5, py: 1, minWidth: 170, zIndex: 1,
                        left: tooltipLeft, transform: tooltipLeft > width / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{longDay(hovered.date)}</Typography>
                        <TooltipRow color={SUBSCRIBED_COLOR} label="Subscribed" value={`+${formatCount(hovered.subscribed)}`} />
                        <TooltipRow color={UNSUBSCRIBED_COLOR} label="Unsubscribed" value={`−${formatCount(hovered.unsubscribed)}`} />
                        <TooltipRow color={TOTAL_COLOR} label="Total" value={formatCount(hovered.total)} line />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {hovered.date === selectedDay ? 'Click to show the whole period' : 'Click to see this day’s players'}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    );
}

function AxisLabel({ y, text }: { y: number; text: string }) {
    return <text x={MARGIN.left - 8} y={y} dy="0.32em" fontSize={11} fill={TEXT_SECONDARY} textAnchor="end">{text}</text>;
}

function LegendSwatch({ color, label, line = false }: { color: string; label: string; line?: boolean }) {
    return (
        <Stack direction="row" alignItems="center" gap={0.75}>
            <Box sx={line
                ? { width: 16, height: 2, bgcolor: color, borderRadius: 1 }
                : { width: 10, height: 10, bgcolor: color, borderRadius: '2px' }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Stack>
    );
}

function TooltipRow({ color, label, value, line = false }: { color: string; label: string; value: string; line?: boolean }) {
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={line
                ? { width: 10, height: 2, bgcolor: color }
                : { width: 8, height: 8, bgcolor: color, borderRadius: '2px' }} />
            <Typography variant="caption" sx={{ flex: 1 }} color="text.secondary">{label}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Stack>
    );
}
